import PanelError from '@wandb/weave/common/components/elements/PanelError';
import React, {memo, useMemo, useState} from 'react';

import {DEFAULT_LINE_PLOT_MAX_GROUP_RUNS} from '../../components/WorkspaceDrawer/Settings/runLinePlots/maxRunDefaults';
import type {
  HighlightSource,
  SetHighlight,
  SetHighlights,
} from '../../state/interactState/highlightSource';
import {IconPositioningContextProvider} from '../../state/issues/IconPositioningContext';
import {type IssueLine} from '../../state/issues/types';
import {prettyXAxisLabel} from '../../util/plotHelpers/axis';
import {
  getPlotFontSize,
  plotFontSizeToPx,
} from '../../util/plotHelpers/plotFontSize';
import {useResizeObserverDebounced} from '../../util/useResizeObserverDebounced';
import {PanelPopupMode} from '../Panel/types';
import {
  PanelTitleView,
  type PanelTitleViewProps,
} from '../PanelTitle/PanelTitleView';
import {LinePlot} from '../vis/LinePlot';
import {BarChart} from './BarChart';
import {CappedPanelDelayRender} from './CappedPanelDelayRender';
import {getTitleFromConfig} from './common';
import {getErrorMessage} from './getGraphError';
import {partitionLines} from './graphLines';
import {IssueMarkerList} from './IssueMarkerList';
import {getIssuesWrapperStatus} from './issueMarkers';
import {type IssueMarkerStateInputs} from './issueMarkerViewProps';
import {PlotLayoutView, type PlotLayoutViewProps} from './Layout';
import {LinePlotLegendView} from './LinePlotLegendView';
import {LinePlotLegendWarningView} from './LinePlotLegendWarningView';
import {
  type LinePlotSettings,
  LinePlotSettingsProvider,
} from './LinePlotSettingsContext';
import {MissingRunBanner} from './MissingRunBanner';
import {
  type LinePlotZoomUsageInputs,
  useLinePlotZoomUsageTracker,
} from './observability/zoomUsage';
import {type PanelConfigContextType} from './PanelConfigContext';
import {
  SamplingWarningView,
  type SamplingWarningViewProps,
} from './SamplingWarningView';
import {type RunsLinePlotPanelInnerProps} from './types';
import {type CappedPanelInputs} from './useCappedPanelOptIn';
import {getIsLegendShown} from './useLegendVisibility';
import {useLinesForDisplay} from './useLinesForDisplay';
import {
  linesHaveRenderableData,
  useRegexMetricSettlingLoading,
} from './useRegexMetricSettlingLoading';
import {isBarPlot} from './utils/checkTypes';
import {getGraphZoom, type GraphZoomInputs} from './zoom/getGraphZoom';

/**
 * Everything Graph needs, as props. GraphContainer is one host: it reads app
 * state and builds each group. Any host can build them instead; the host
 * contract in GRAPH_ARCHITECTURE.md lists what a host must provide. Every
 * group is required.
 *
 * Graph and GraphWrapper are memoized, so they re-render when any prop
 * changes identity. Each group must keep its identity while its fields are
 * unchanged. GraphContainer builds each one as a named const that React
 * Compiler caches on its fields.
 *
 * From the panel props, Graph reads `config` (the saved panel config, whose
 * `xAxis` is the raw saved value; see plotSettings.xAxis. The no-data
 * message shows config.xAxis), `lines`, `loading`,
 * `data` (`histories` must be non-null; the run counts feed the legend
 * warning), `hasError`, `readOnly`, `exportMode`, `runNameTruncationType`, and
 * `disableRunLinks` (set it when the host renders outside a router). These
 * are per-render data: a new identity re-renders Graph. BarChart receives
 * all props and reads `lines` and `config`.
 */
export type GraphProps = RunsLinePlotPanelInnerProps & {
  /**
   * Delay Render opt-in for panels with capped system metrics, from
   * PanelSystemMetricsContext. A cached group: `isDelayed` is state, and
   * `setAllowUncappedMetrics` is a stable setter that Graph calls with true
   * when the user clicks Load anyway. Changing `isDelayed` swaps the plot for
   * the warning. Graph adds the title from config and `panelId`.
   */
  cappedPanel: Omit<CappedPanelInputs, 'panelId' | 'title'>;
  /**
   * Display settings only Graph reads, from PanelConfigContext. A cached
   * group of values. They stay out of plotSettings, so changing one
   * re-renders Graph and the plot but not the settings readers.
   */
  display: Pick<
    PanelConfigContextType,
    'excludeOutliers' | 'showLegend' | 'singlePointRenderMode'
  >;
  /**
   * Cross-panel highlight: a source over the Redux `interactState` highlight
   * and its two writers. GraphContainer takes the store's one source and the
   * writers from the `interactState` hooks. All three are stable for the
   * panel's life, so a highlight change never re-renders Graph; readers below
   * subscribe to the axes they draw. Hovering a legend run calls
   * `setHighlight`, and moving the pointer over the chart calls
   * `setHighlights`. The writers must update the source, or the crosshair
   * never shows its flag. Panels that share a crosshair share one source.
   */
  highlight: {
    setHighlight: SetHighlight;
    setHighlights: SetHighlights;
    source: HighlightSource;
  };
  /**
   * Issue lines drawn on the chart and positioned as markers above it, from
   * GraphContainer's caller. Per-render data: a new array re-renders Graph,
   * the chart, and the markers. Defaults to none.
   */
  issueLines?: IssueLine[];
  /**
   * Issue state and actions for the markers above the plot. GraphContainer
   * reads the issues store, the route's entity and project for analytics, the
   * workspace drawer, and the query zoom's x bounds. A cached group: the
   * count, flags, and bounds are state; `hoveredIssueSource` is a stable
   * source; the functions are stable. Hovering a marker calls
   * `handleIssueInteraction`, and clicking one opens the run issues drawer
   * and calls `setSelectedIssue`. A state change re-renders Graph and the
   * markers; a hovered-issue change re-renders only the markers whose state
   * changes. Marker layout comes from icon positioning inside Graph.
   */
  issueMarkers: IssueMarkerStateInputs;
  /**
   * ID of the mounted panel, from PanelIDContext. Stable for the panel's
   * life and unique per mounted panel: it keys the error panel's RUM
   * de-duplication, the zoom usage counter, and chart telemetry, and is sent
   * with the Delay Render RUM actions. The only copy in GraphProps.
   */
  panelId: string;
  /**
   * What the panel title needs. GraphContainer reads the full-screen state,
   * the debounced panel search, and the panel context. A cached group:
   * `makeHighlightSearchTitle` changes with the search text, and
   * `setPanelPopupMode` is a stable setter that Graph calls with EDIT when
   * the user clicks the title. A change re-renders Graph and the title only.
   * A host without search or an editor passes undefined and a no-op.
   */
  panelTitle: {
    isFullScreen: boolean;
    makeHighlightSearchTitle: PanelTitleViewProps['highlightSearch'];
    setPanelPopupMode: (mode: PanelPopupMode) => void;
  };
  /**
   * Settings for Graph, the renderers, the crosshair, the legend, and the
   * legend warning; the one source for each. GraphContainer resolves them
   * from the panel config, workspace and section settings, flags, color mode,
   * and the nav route. A cached group: its identity changes only when a field
   * does. GraphWrapper provides it inside the unit through
   * LinePlotSettingsProvider, so a change re-renders Graph and every
   * useLinePlotSettings reader; the legend and warning views re-render only
   * when their own fields change. It also holds one event,
   * `onSnapMediaPanelsToStep`, and one getter, `getNamedWorkspaceSearch`;
   * both must be stable.
   */
  plotSettings: LinePlotSettings;
  /**
   * Whether the panel fell back to sampling, and why, from
   * RunsLinePlotContext. A cached group; a change re-renders Graph and the
   * sampling warning only.
   */
  samplingWarning: Pick<
    SamplingWarningViewProps,
    'isSamplingFallback' | 'warningMessages'
  >;
  /**
   * Ref Graph attaches to the layout grid so the host can measure the panel,
   * from PanelInteractionContext. Stable for the panel's life; null is
   * accepted.
   */
  sizeRef: PlotLayoutViewProps['sizeRef'];
  /**
   * Both zoom systems and the zoom writers. GraphContainer reads the panel
   * zoom, time, shared zoom, and interaction contexts; getGraphZoom picks a
   * system by plotSettings.isFullFidelity. A cached group whose setters are
   * stable. A zoom gesture calls the setters, so zoom works only when the
   * host feeds their results back into this group. A change re-renders Graph
   * and the plot.
   */
  zoom: GraphZoomInputs;
  /**
   * Where the panel is mounted, for the zoom usage RUM action, from the
   * report context and the full-screen state. A cached group of booleans; a
   * change re-renders Graph and affects only telemetry. Graph adds panelId.
   */
  zoomUsage: Omit<LinePlotZoomUsageInputs, 'panelId'>;
};

const expandStyles: React.CSSProperties = {
  height: '100%',
  width: '100%',
};

const SAMPLING_WARNING_STYLE: React.CSSProperties = {zIndex: 1};

/**
 * Highest per-runset run count — the signal the multi-runset warning path
 * keys off of. `Math.max(...[])` is `-Infinity`, so guard on empty map.
 */
function getMaxFetchedRunCount(
  countsByRunsetId: Record<string, number> | undefined
): number {
  const values = Object.values(countsByRunsetId ?? {});
  if (values.length === 0) {
    return 0;
  }
  return Math.max(...values);
}

const GraphComp: React.FC<GraphProps> = props => {
  // destructure
  const {
    lines,
    config,
    issueLines = [],
    loading,
    runNameTruncationType,
  } = props;

  // state
  const [isHovered, setIsHovered] = useState(false);

  const {
    isResizing,
    height = 1,
    ref: elementRef,
    width = 1,
  } = useResizeObserverDebounced<HTMLDivElement>(1);
  const {excludeOutliers, singlePointRenderMode, showLegend} = props.display;
  const {
    isFullFidelity,
    isMultiMetric,
    isSingleRun,
    xAxis,
    xLogScale,
    yLogScale,
  } = props.plotSettings;
  // Issue lines past a zoomed x bound are hidden, and a zoomed x axis skips
  // the domain mismatch check.
  const {queryZoom} = props.zoom.fullFidelity;
  const isZoomedXMin = queryZoom.xAxisMin != null;
  const isZoomedXMax = queryZoom.xAxisMax != null;

  const fontSize = getPlotFontSize(config.fontSize ?? 'auto', height);

  // Additional constraints are applied for legends depending on panel width/height and if
  // view is single run
  const isLegendShown = getIsLegendShown({
    isMultiMetric,
    isSingleRun,
    panelSize: {height, width},
    showLegend,
  });

  const timestep = lines?.[0]?.timestep;

  const xScale = xLogScale ? 'log' : 'linear';
  const yScale = yLogScale ? 'log' : 'linear';

  const {filteredLines} = useLinesForDisplay(lines, {
    xScale,
    yScale,
    isFullFidelity,
    isHovered,
  });
  const isRegexMetricSettling = useRegexMetricSettlingLoading(
    config,
    filteredLines
  );
  const effectiveLoading = loading || isRegexMetricSettling;

  const {legendLines, visibleLines, hiddenLines} = useMemo(
    function partitionDisplayLines() {
      return partitionLines(filteredLines);
    },
    [filteredLines]
  );
  const trackZoomUsage = useLinePlotZoomUsageTracker({
    ...props.zoomUsage,
    lines: visibleLines,
    metrics: config.metrics,
    panelId: props.panelId,
    xAxis,
  });
  /**
   * Notes on timestep:
   * 1. Timestep is determined by examining the lines data and determining a proper scale
   * 2. The timestep is saved across the panel through the interaction context
   * 3. when we zoom we don't want to change the timestep factor
   */
  const {activeZoom, xDomain, yDomain, zoomCallback, zooming} = getGraphZoom(
    props.zoom,
    props.plotSettings,
    timestep,
    trackZoomUsage
  );

  // derived values
  const [errorDetail, errorMessage] = getErrorMessage(
    config,
    props.cappedPanel.isDelayed,
    filteredLines,
    {
      activeZoom,
      zoomCallback,
    }
  );

  const renderBarChart = isBarPlot(props.lines, zooming, singlePointRenderMode);
  const renderPanelError = !effectiveLoading && errorMessage != null;

  const [isDrawing, setIsDrawing] = useState(false);

  const panelErrorMetaInfo = {isFullFidelity};

  // A full-screen panel shows its title without search highlighting.
  const highlightTitleSearch = props.panelTitle.isFullScreen
    ? undefined
    : props.panelTitle.makeHighlightSearchTitle;
  function openPanelEditor() {
    props.panelTitle.setPanelPopupMode(PanelPopupMode.EDIT);
  }
  // Hovering a legend run name highlights that run across panels.
  function highlightHoveredRun(
    runId: string | undefined,
    metricName: string | undefined
  ) {
    props.highlight.setHighlight('run:name', runId, metricName);
  }

  const warning = (
    <LinePlotLegendWarningView
      groupingDepth={props.plotSettings.groupingDepth}
      isFullFidelity={props.plotSettings.isFullFidelity}
      isGrouped={props.plotSettings.isAnyRunsetGrouped}
      maxRuns={props.plotSettings.limit}
      hasSubsampledGroups={props.data.hasSubsampledGroups ?? false}
      isMultiMetric={isMultiMetric ?? false}
      loading={effectiveLoading}
      metrics={config.metrics?.length ?? 0}
      isReadOnly={props.readOnly ?? false}
      lines={legendLines}
      maxFetchedRunCount={getMaxFetchedRunCount(
        props.data.fetchedRunCountByRunsetId
      )}
      maxGroupRuns={config.groupRunsLimit ?? DEFAULT_LINE_PLOT_MAX_GROUP_RUNS}
      runCount={props.data.fetchedRunCount ?? 0}
      runsetCount={
        Object.keys(props.data.fetchedRunCountByRunsetId ?? {}).length
      }
      totalGroupedIncludedRuns={props.data.totalGroupedIncludedRuns ?? 0}
      disallowSelect={isDrawing}
    />
  );

  if (
    renderBarChart ||
    renderPanelError ||
    (props.hasError && !effectiveLoading)
  ) {
    return (
      <div
        className="chart"
        data-test={`runs-line-plot-error-${
          props.config.xAxis ?? 'unknown-x-axis'
        }`}
        onMouseEnter={function markErrorPanelHovered() {
          setIsHovered(true);
        }}
        onMouseLeave={function clearErrorPanelHovered() {
          setIsHovered(false);
        }}
        ref={elementRef}>
        <PanelTitleView
          title={getTitleFromConfig(config)}
          fontSize={fontSize}
          highlightSearch={highlightTitleSearch}
          onTitleClick={openPanelEditor}
        />
        {renderBarChart && warning}
        <div className="chart-content" data-test="chart-content">
          {renderPanelError ? (
            <PanelError
              panelViewType="Run History Line Plot"
              panelID={props.panelId}
              metaInfo={panelErrorMetaInfo}
              message={
                props.hasError ? (
                  <div>
                    An unexpected error was detected. Will try again shortly.
                  </div>
                ) : (
                  errorMessage
                )
              }
              logErrorMessage={
                props.hasError
                  ? 'An unexpected error was detected. Will try again shortly.'
                  : typeof errorDetail === 'string'
                    ? errorDetail
                    : 'Chart error occurred'
              }
            />
          ) : (
            <BarChart {...props} />
          )}
        </div>
      </div>
    );
  }

  const issueStatus = getIssuesWrapperStatus({
    annotationActive: props.issueMarkers.annotationActive,
    issueCount: props.issueMarkers.activeIssueCount,
    issueRampStatus: props.issueMarkers.issueRampStatus,
  });

  return (
    <div
      data-x-axis={config.xAxis ?? 'unknown-x-axis'}
      data-test="chart-content"
      ref={elementRef}
      style={expandStyles}>
      <IconPositioningContextProvider issues={issueLines} width={width}>
        <PlotLayoutView
          delayed={props.cappedPanel.isDelayed}
          DelayWarning={
            <CappedPanelDelayRender
              {...props.cappedPanel}
              panelId={props.panelId}
              title={getTitleFromConfig(config)}
            />
          }
          IssueMarkers={<IssueMarkerList {...props.issueMarkers} />}
          issueStatus={issueStatus}
          fontSize={plotFontSizeToPx.legend[fontSize]}
          handleHover={setIsHovered}
          loading={effectiveLoading}
          numLines={
            linesHaveRenderableData(visibleLines) ? visibleLines.length : 0
          }
          Graph={
            isResizing ? (
              <div />
            ) : (
              <LinePlot
                excludeOutliers={excludeOutliers}
                filteredLines={visibleLines}
                fontSize={fontSize}
                highlightSource={props.highlight.source}
                isDrawing={isDrawing}
                isHovered={isHovered}
                issueLines={issueLines}
                hoveredIssueSource={props.issueMarkers.hoveredIssueSource}
                isZoomedXMax={isZoomedXMax}
                isZoomedXMin={isZoomedXMin}
                panelId={props.panelId}
                runNameTruncationType={runNameTruncationType}
                setHighlights={props.highlight.setHighlights}
                setIsDrawing={setIsDrawing}
                svg={props.exportMode}
                timestep={timestep}
                xAxis={prettyXAxisLabel(xAxis, props.lines)}
                xAxisTitle={config.xAxisTitle}
                xDomain={xDomain}
                xScale={xScale}
                yAxis={''}
                yAxisTitle={config.yAxisTitle}
                yDomain={yDomain}
                yScale={yLogScale ? 'log' : 'linear'}
                zoomCallback={zoomCallback}
                zooming={zooming}>
                {
                  <MissingRunBanner
                    highlightSource={props.highlight.source}
                    lines={visibleLines}
                  />
                }
              </LinePlot>
            )
          }
          legendPosition={config.legendPosition ?? 'north'}
          Legend={
            <LinePlotLegendView
              colorPalette={props.plotSettings.colorPaletteOption}
              colorRunNames={props.plotSettings.colorRunNames}
              disableRunLinks={props.disableRunLinks}
              fontSize={fontSize}
              hiddenLines={hiddenLines}
              isMultiMetric={props.plotSettings.isMultiMetric}
              isSingleRun={props.plotSettings.isSingleRun}
              lines={legendLines}
              isVertical={
                !['east', 'west'].includes(config.legendPosition ?? '')
              }
              disallowSelect={isDrawing}
              onRunHover={highlightHoveredRun}
            />
          }
          showLegend={isLegendShown}
          sizeRef={props.sizeRef}
          Title={
            <PanelTitleView
              title={getTitleFromConfig(config)}
              fontSize={fontSize}
              disallowSelect={isDrawing}
              highlightSearch={highlightTitleSearch}
              onTitleClick={openPanelEditor}
            />
          }
          Warning={warning}
        />
      </IconPositioningContextProvider>
      <SamplingWarningView
        {...props.samplingWarning}
        style={SAMPLING_WARNING_STYLE}
        width={width}
      />
    </div>
  );
};
const Graph = memo(GraphComp);
Graph.displayName = 'Graph';

// TODO(axel): This wrapper is only here because I'm not confident that some props are guaranteed to exist.
// Find out what's up and kill this wrapper.
const GraphWrapperComp: React.FC<GraphProps> = function GraphWrapperComp(
  props
) {
  if (props.data.histories == null) {
    return <p>No Histories</p>;
  }

  return (
    <LinePlotSettingsProvider settings={props.plotSettings}>
      <Graph {...props} />
    </LinePlotSettingsProvider>
  );
};
export const GraphWrapper = memo(GraphWrapperComp);
GraphWrapper.displayName = 'GraphWrapper';
